import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { AppDispatch } from '../../../../store';
import AlertPopup from '../../../../components/common/AlertPopup';
import ActionModal from '../../../../components/ActionModal';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import { fetchShiftDetails } from '../../../../modules/facilityShifts/actions';
import { selectShiftDetailsData, selectShiftDetailsLoading } from '../../../../modules/facilityShifts/selectors';
import { updateWorkerViolationFlag } from '../../../../modules/worker';

import { Divider } from './styles';

import WorkerAssigned from './components/WorkerAssigned';
import AssignedWorkerList from './components/AssignedWorkerList';
import FacilityDetails from './components/FacilityDetails';
import ShiftDetails from './components/ShiftDetails';
import { Box, CircularProgress, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Using local VerifyShift
// Using local VerifyShift
import VerifyShift from '../VerifyShift';

interface ShiftDetailContentProps {
  shiftId: number | null;
  facilityName?: string;
  onEdit?: (shift: any) => void;
  onRefresh?: () => void;
}

const ShiftDetailContent: React.FC<ShiftDetailContentProps> = ({ shiftId, facilityName, onRefresh }) => {
  const dispatch = useDispatch<AppDispatch>();
  const shift = useSelector(selectShiftDetailsData);
  const loading = useSelector(selectShiftDetailsLoading);
  const [view, setView] = React.useState<'details' | 'worker_detail'>('details');
  const [selectedAssignment, setSelectedAssignment] = React.useState<any>(null);
  const [alertState, setAlertState] = React.useState({ open: false, severity: 'success' as 'success' | 'error', message: '' });

  const [isVerifyModalOpen, setIsVerifyModalOpen] = React.useState(false);

  // Flag Worker State
  const [flagConfirmationOpen, setFlagConfirmationOpen] = React.useState(false);
  const [workerToFlag, setWorkerToFlag] = React.useState<number | null>(null);
  const [isFlagging, setIsFlagging] = React.useState(false);

  const handleFlagWorkerClick = (workerId: number) => {
    setWorkerToFlag(workerId);
    setFlagConfirmationOpen(true);
  };

  const confirmFlagWorker = () => {
    if (workerToFlag) {
      setIsFlagging(true);
      // @ts-ignore
      dispatch(updateWorkerViolationFlag({ workerId: workerToFlag, violation_flag: true }))
        .unwrap()
        .then((res: { data: { message: any; }; }) => {
          setFlagConfirmationOpen(false);
          setAlertState({ open: true, severity: 'success', message: res.data?.message || 'Worker flagged successfully' });
          if (shiftId) dispatch(fetchShiftDetails(shiftId));
        })
        .catch((err: any) => {
          setAlertState({ open: true, severity: 'error', message: String(err) });
        })
        .finally(() => {
          setIsFlagging(false);
        });
    }
  };

  const handleWorkerClick = (assignment: any) => {
    setSelectedAssignment(assignment);
    setView('worker_detail');
  };

  useEffect(() => {
    if (shiftId) {
      dispatch(fetchShiftDetails(shiftId));
      setView('details');
    }
  }, [shiftId, dispatch]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shift || !shiftId) return null;

  if (view === 'worker_detail' && selectedAssignment) {
    const user = selectedAssignment.worker.user || selectedAssignment.user || {};
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown Worker';

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={1}>
          <IconButton onClick={() => setView('details')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="bold" ml={1}>
            Shift Details
          </Typography>
        </Box>

        <WorkerAssigned
          assignedWorkerName={name}
          positionRole={shift.position_role}
          workerId={selectedAssignment.worker?.worker_id}
          avatarSrc={user.profile_picture}
          status={selectedAssignment.status === 'worker_cancelled' ? 'Worker Cancelled' : (selectedAssignment.status === 'facility_reject' ? 'Facility Reject' : (selectedAssignment.late_arrival ? 'Late' : 'On Time'))}
          isLate={selectedAssignment.status === 'facility_reject' || selectedAssignment.late_arrival || selectedAssignment.status === 'worker_cancelled'}
          onFlagWorker={
            (selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              ? () => handleFlagWorkerClick(selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              : undefined
          }
          workerRemarks={selectedAssignment.status === 'worker_cancelled' ? (selectedAssignment.worker_remarks || selectedAssignment.remarks) : undefined}
        />
        <Divider />
        <FacilityDetails
          facilityName={facilityName || String(shift.facility)}
          address={(shift.facility as any)?.address}
          latitude={(shift.facility as any)?.latitude}
          longitude={(shift.facility as any)?.longitude}
        />
        <Divider />
        <ShiftDetails
          date={dayjs(shift.start_date_time).format('DD/MM/YYYY')}
          createdDate={shift.created_on ? dayjs(shift.created_on).format('DD/MM/YYYY') : undefined}
          assignedDate={selectedAssignment.created_on ? dayjs(selectedAssignment.created_on).format('DD/MM/YYYY') : undefined} // Mock assignment date
          startTime={dayjs(shift.start_date_time).format('HH.mm')}
          endTime={dayjs(shift.end_date_time).format('HH.mm')}
          numberOfWorkers={`${shift.shift_assignment?.filter((a: any) => a.status !== 'worker_cancelled').length || 0}/${shift.workers_required}`}
          experience={0}
          appointedMember={"-"}
          position={shift.position_role || "-"}
          shift_id={shiftId}
          // Detailed Props for specific worker
          assignmentStatus={(['partially_approval_pending', 'approval_pending', 'facility_approval_pending'].includes(selectedAssignment.status) ? 'Facility Approval Pending' : selectedAssignment.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())) || '-'}
          isFacilityReject={selectedAssignment.status === 'facility_approval_pending'}
          onApproveAttendance={selectedAssignment.status === 'facility_approval_pending' ? () => setIsVerifyModalOpen(true) : undefined}
          complianceStatus={(selectedAssignment.status === 'facility_reject' || selectedAssignment.late_arrival || selectedAssignment.left_early) ? 'Non-Compliant' : 'Compliant'}
          clockInTime={selectedAssignment.clock_in_time ? dayjs(selectedAssignment.clock_in_time).format('HH.mm') : '-'}
          clockOutTime={selectedAssignment.clock_out_time ? dayjs(selectedAssignment.clock_out_time).format('HH.mm') : '-'}
          totalHours={
            selectedAssignment.actual_hours_worked
              ? `${selectedAssignment.actual_hours_worked} hours`
              : `${dayjs(shift.end_date_time).diff(dayjs(shift.start_date_time), 'hour', true).toFixed(1)} hours`
          }
          checkInMethod={"Mobile App (Android)"}
          isLate={selectedAssignment.late_arrival}
          isEarly={selectedAssignment.left_early}
          workerRemarks={selectedAssignment.status === 'worker_cancelled' ? (selectedAssignment.worker_remarks || selectedAssignment.remarks) : undefined}
        />
        <ActionModal
          open={isVerifyModalOpen}
          onClose={() => setIsVerifyModalOpen(false)}
          height="auto"
        >
          {selectedAssignment && shiftId && (
            <VerifyShift
              clockIn={selectedAssignment.clock_in_time}
              clockOut={selectedAssignment.clock_out_time}
              workerRemarks={selectedAssignment.worker_remarks}
              workerSuggestedTime={selectedAssignment.worker_suggested_time}
              assignmentId={selectedAssignment.assignment_id || selectedAssignment.id}
              shiftId={Number(shiftId)}
              shiftStart={shift.start_date_time}
              shiftEnd={shift.end_date_time}
              onSuccess={(action: 'accept' | 'reject', message?: string) => {
                setIsVerifyModalOpen(false);
                if (shiftId) dispatch(fetchShiftDetails(shiftId));
                onRefresh && onRefresh();
                setAlertState({ open: true, severity: 'success', message: message || 'Attendance verified successfully' });
              }}
            />
          )}
        </ActionModal>
        <ConfirmationDialog
          open={flagConfirmationOpen}
          onClose={() => setFlagConfirmationOpen(false)}
          onConfirm={confirmFlagWorker}
          title="Flag Worker"
          message="Are you sure you want to flag this worker? This action will mark a violation against the worker."
          loading={isFlagging}
        />
        <AlertPopup
          open={alertState.open}
          severity={alertState.severity}
          message={alertState.message}
          onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
        />
      </Box>
    );
  }

  // List View (Base)
  return (
    <Box>
      <ShiftDetails
        date={dayjs(shift.start_date_time).format('DD/MM/YYYY')}
        createdDate={shift.created_on ? dayjs(shift.created_on).format('DD/MM/YYYY') : undefined}
        assignedDate={shift.shift_assignment?.length > 0 && shift.shift_assignment[0].created_on ? dayjs(shift.shift_assignment[0].created_on).format('DD/MM/YYYY') : undefined}
        startTime={dayjs(shift.start_date_time).format('HH.mm')}
        endTime={dayjs(shift.end_date_time).format('HH.mm')}
        numberOfWorkers={`${shift.shift_assignment?.filter((a: any) => a.status !== 'worker_cancelled').length || 0}/${shift.workers_required}`}
        experience={0}
        appointedMember={"-"}
        position={shift.position_role || "-"}
        shift_id={shiftId}
      />
      <Divider />
      <FacilityDetails
        facilityName={facilityName || String(shift.facility)}
        address={(shift.facility as any)?.address}
        latitude={(shift.facility as any)?.latitude}
        longitude={(shift.facility as any)?.longitude}
      />
      <Divider />
      {/* Pass onWorkerClick to allow drill-down */}
      <AssignedWorkerList
        shift={shift}
        onWorkerClick={handleWorkerClick}
        variant="simple"
      />
      
      <ConfirmationDialog
        open={flagConfirmationOpen}
        onClose={() => setFlagConfirmationOpen(false)}
        onConfirm={confirmFlagWorker}
        title="Flag Worker"
        message="Are you sure you want to flag this worker? This action will mark a violation against the worker."
        loading={isFlagging}
      />
      <AlertPopup
        open={alertState.open}
        severity={alertState.severity}
        message={alertState.message}
        onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
      />
    </Box>
  )
};

export default ShiftDetailContent;
